import { useState, useCallback, useEffect, type PropsWithChildren } from 'react';
import ServerErrorModal from './ServerErrorModal';
import {
  subscribeErrorModal,
  beginRetryPhase,
  endRetryPhase,
  fireRetryCallbacks,
  type ShowErrorModalDetails,
} from './errorModalEmitter';

type ModalState = 'idle' | 'visible' | 'retrying';

function deriveErrorType(
  status: number | undefined
): 'maintenance' | 'server-error' {
  if (status === 415 || (status !== undefined && status >= 500)) {
    return 'server-error';
  }
  return 'maintenance';
}

export const ErrorModalProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [errorStatus, setErrorStatus] = useState<number | undefined>();
  const [isNotRetryable, setIsNotRetryable] = useState(false);

  const handleIncomingError = useCallback((details: ShowErrorModalDetails) => {
    if (details.isSilent === true) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    setErrorStatus(details.status);
    setModalState((prev) => (prev === 'idle' ? 'visible' : prev));
  }, []);

  useEffect(() => {
    return subscribeErrorModal(handleIncomingError);
  }, [handleIncomingError]);

  // "Try Again" uses refetchQueries which only retries React Query-managed
  // queries. If there are no errored RQ queries the button would do nothing
  // meaningful, so we show a simple "OK" instead. This covers:
  //   - mutation-only errors (mutation retry disabled pending client approval)
  //   - direct handleRequest calls from page components that bypass React Query
  useEffect(() => {
    if (modalState !== 'visible') return;

    const checkRetryable = async (): Promise<void> => {
      const { queryClient } = await import('../../queryClient');

      const hasErroredQueries = queryClient
        .getQueryCache()
        .findAll()
        .some((q) => q.isActive() && q.state.status === 'error');

      setIsNotRetryable(!hasErroredQueries);
    };

    void checkRetryable();
  }, [modalState]);

  const handleRetry = useCallback(async () => {
    setModalState('retrying');

    try {
      const { queryClient } = await import('../../queryClient');

      beginRetryPhase();

      // TODO: Re-enable mutation retry after client approval.
      // const mutationCache = queryClient.getMutationCache();
      // const failedMutations = mutationCache
      //   .getAll()
      //   .filter((m) => m.state.status === 'error');
      //
      // const mutationRetries = failedMutations.map((mutation) =>
      //   mutation.execute().catch(() => {})
      // );

      // Only retry queries that are not already successful.
      // Errored queries may have transitioned to 'loading' by the time the
      // user clicks "Try Again", so we skip only 'success' queries.
      const queryRetry = queryClient.refetchQueries({
        predicate: (query) => query.isActive() && query.state.status !== 'success',
      });

      fireRetryCallbacks();

      await Promise.allSettled([queryRetry]);

      const errorsWhileRetrying = endRetryPhase();

      if (errorsWhileRetrying > 0) {
        setModalState('visible');
      } else {
        setModalState('idle');
        setErrorStatus(undefined);
        setIsNotRetryable(false);
      }
    } catch {
      endRetryPhase();
      setModalState('visible');
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setModalState('idle');
    setErrorStatus(undefined);
    setIsNotRetryable(false);
  }, []);

  return (
    <>
      {children}
      {modalState !== 'idle' && (
        <ServerErrorModal
          isRetrying={modalState === 'retrying'}
          onDismiss={handleDismiss}
          onRetry={handleRetry}
          errorType={deriveErrorType(errorStatus)}
          isNotRetryable={isNotRetryable}
        />
      )}
    </>
  );
};
